import { CSSProperties, FC, ReactNode } from 'react';
import { Avatar, Button, Col, Row } from 'antd';
import { capitalCase } from 'change-case';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { useWorkspaceResourceCreateAccess, WorkspaceCreatableResource } from '@/hooks/useWorkspaceResourceCreateAccess';
import { WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';
import { useMemoStore } from '@/stores';
import { centerComponentInMiddleofView } from '@/utilities';

interface EmptyWorkspaceResourceListViewProps {
    resourceType: 'folder' | 'investment' | 'memo' | 'pipeline' | 'dashboard' | 'list' | 'team';
    height?: CSSProperties['height'];
}

const EmptyWorkspaceResourceListView: FC<EmptyWorkspaceResourceListViewProps> = ({
    resourceType,
    height,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    const { workspaceResourceToCreationIconMap, singularCreationModalMap, setCreationModalOpen } = useMaps();

    const { createMemo } = useMemoStore();
    const navigate = useNavigate();
    const resourceToUsageResourceMap: Record<typeof resourceType, WorkspaceCreatableResource> = {
        folder: 'folders',
        investment: 'investments',
        memo: 'memos',
        pipeline: 'pipelines',
        dashboard: 'dashboards',
        list: 'lists',
        team: 'teams',
    };

    const createAccess = useWorkspaceResourceCreateAccess(resourceToUsageResourceMap[resourceType]);
    const createButtonProps = {
        disabled: createAccess.disabled,
        style: { padding: '0.5rem' },
        type: 'primary' as const,
    };

    const actionsMap = {
        folder: (
            <Row>
                <Col flex='none'>
                    <Button {...createButtonProps} onClick={() => setCreationModalOpen(true)}>
                        {workspaceResourceToCreationIconMap[resourceType]}
                        New Folder
                    </Button>
                </Col>
            </Row>
        ),
        investment: (
            <Row>
                <Col flex='none'>
                    <Button {...createButtonProps} onClick={() => setCreationModalOpen(true)}>
                        {workspaceResourceToCreationIconMap[resourceType]}
                        New Investment
                    </Button>
                </Col>
            </Row>
        ),
        memo: (
            <Row>
                <Col flex='none'>
                    <Button
                        {...createButtonProps}
                        onClick={() =>
                            !createAccess.disabled &&
                            createMemo({ title: 'Untitled Memo' }).then((m) => navigate(`/memos/${m?.id}`))
                        }
                    >
                        {workspaceResourceToCreationIconMap[resourceType]}
                        New Memo
                    </Button>
                </Col>
            </Row>
        ),
        pipeline: (
            <Row>
                <Col flex='none'>
                    <Button {...createButtonProps} onClick={() => setCreationModalOpen(true)}>
                        {workspaceResourceToCreationIconMap[resourceType]}
                        New Pipeline
                    </Button>
                </Col>
            </Row>
        ),
        dashboard: (
            <Row>
                <Col flex='none'>
                    <Button {...createButtonProps} onClick={() => setCreationModalOpen(true)}>
                        {workspaceResourceToCreationIconMap[resourceType]}
                        New Dashboard
                    </Button>
                </Col>
            </Row>
        ),
        list: (
            <Row>
                <Col flex='none'>
                    <Button {...createButtonProps} onClick={() => setCreationModalOpen(true)}>
                        {workspaceResourceToCreationIconMap[resourceType]}
                        New List
                    </Button>
                </Col>
            </Row>
        ),
        team: (
            <Row>
                <Col flex='none'>
                    <Button {...createButtonProps} onClick={() => setCreationModalOpen(true)}>
                        {workspaceResourceToCreationIconMap[resourceType]}
                        New Team
                    </Button>
                </Col>
            </Row>
        ),
    } as Record<typeof resourceType, ReactNode>;

    const descriptionMaps = {
        folder: (
            <>
                Folders help institutional teams maintain clean information architecture across investments, research
                artifacts, and supporting materials, making knowledge easier to navigate and scale over time.
            </>
        ),
        investment: (
            <>
                Investments serve as the central system of record for your team’s underwriting work, supporting
                financial analysis, document review, discussion, and ongoing monitoring across the full lifecycle of a
                deal.
            </>
        ),
        memo: (
            <>
                Memos help institutional teams document conviction, capture key risks, and create an auditable record of
                investment decision-making across the research and approval process.
            </>
        ),
        pipeline: (
            <>
                Pipelines provide structured workflow visibility from initial sourcing through diligence and decision,
                helping teams track momentum, identify bottlenecks, and maintain consistent process discipline.
            </>
        ),
        dashboard: (
            <>
                Dashboards allow you to build real-time monitoring and analytical views across your portfolio and
                research universe by consolidating key metrics, operating data, and market signals into a single
                decision space.
            </>
        ),
        list: (
            <>
                Lists act as flexible building blocks for screening, tracking, and segmenting your research universe
                across sourcing and monitoring workflows.
            </>
        ),
        team: (
            <>
                Teams enable secure collaboration across your organization, ensuring the right stakeholders have
                appropriate visibility and editing rights across sensitive research, diligence, and portfolio materials.
            </>
        ),
    } as Record<typeof resourceType, ReactNode>;

    return (
        <div
            style={{
                ...centerComponentInMiddleofView,
                boxSizing: 'border-box',
                height: height ?? '100%',
                minHeight: 0,
            }}
        >
            <Avatar
                size={16 * 2.5}
                shape='square'
                style={{ border: `1px solid ${token.colorBorder}` }}
                icon={
                    <WorkspaceResourceGlyph
                        color={token.colorTextQuaternary}
                        resource={resourceType === 'folder' ? 'folders' : resourceType}
                        size='67.5%'
                    />
                }
            />
            <div
                style={{
                    margin: '0.5rem 0',
                    marginTop: '0.75rem',
                    color: token.colorTextSecondary,
                    fontWeight: 500,
                }}
            >
                No {resourceType && capitalCase(resourceType)}s
            </div>
            <div style={{ marginBottom: '1rem', width: '20rem', textAlign: 'center' }}>
                <small style={{ color: token.colorTextTertiary }}>{descriptionMaps[resourceType]}</small>
            </div>
            {actionsMap[resourceType]}
            {singularCreationModalMap[resourceType]}
        </div>
    );
};

export default EmptyWorkspaceResourceListView;
